import Button from '@rio-cloud/rio-uikit/Button';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import classNames from 'classnames';
import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAllowAssetDeleteWithActiveContractsToggle } from '../../../../../configuration/featureToggle/toggleHooks';
import { useIsLatamTenant, useIsTestTenant } from '../../../../../configuration/login/loginSlice';
import { useGetActiveContractsQuery } from '../../../../services/contractsApi';
import { useCheckActiveContractsQuery } from '../../../../services/latamContractsApi';
import { AssetStatus } from '../types';
import { ActiveContractsWarning } from './ActiveContractsWarning';
import type { SidebarFooterProperties } from './types';
import ButtonToolbar from '@rio-cloud/rio-uikit/ButtonToolbar';

export const SidebarFooter = (props: SidebarFooterProperties) => {
    const intl = useIntl();
    const {
        closeDeleteDialog,
        openDeleteDialog,
        displayDeleteDialog,
        statusActionDisabled,
        modalDeleteButtonDisabled,
        submitButtonClick,
        saveButtonDisabled,
        deletionErrorMessageCode,
        loadingOnSave,
        deleteAsset,
        updatedAsset,
        currentAssetStatus,
        onStatusActionClick,
        toggleUnsavedAssetChanges,
    } = props;
    const isTestTenant = useIsTestTenant();
    const isLatamTenant = useIsLatamTenant();
    const { data: activeContracts } = useGetActiveContractsQuery(updatedAsset.id, { skip: isLatamTenant });
    const { data: activeLatamContracts } = useCheckActiveContractsQuery(updatedAsset.id, { skip: !isLatamTenant });
    const allowAssetDeleteWithActiveContracts = useAllowAssetDeleteWithActiveContractsToggle();
    const hasActiveContracts = [activeContracts, activeLatamContracts].some(c => {
        return Array.isArray(c) && c.length > 0;
    });

    const submitButtonClickHandler = useCallback(() => {
        submitButtonClick();
        toggleUnsavedAssetChanges(false);
    }, [submitButtonClick, toggleUnsavedAssetChanges]);
    const statusActionButtonLabel =
        currentAssetStatus === AssetStatus.active
            ? intl.formatMessage({ id: 'intl-msg:asset.archive', defaultMessage: 'Archive' })
            : intl.formatMessage({ id: 'intl-msg:asset.activate', defaultMessage: 'Activate' });
    const statusActionButtonIconName = currentAssetStatus === AssetStatus.active ? 'lock' : 'lock-open';

    const buttonClassNames = classNames(loadingOnSave && 'btn-loading');

    const leftActions = (
        <>
            <Button
                bsStyle={Button.DANGER}
                variant={Button.VARIANT_LINK}
                disabled={loadingOnSave || deletionErrorMessageCode != null}
                iconName='trash'
                autoTrackingKey='intl-msg:asset.delete'
                onClick={openDeleteDialog}
                data-cy='detail-view-delete-button'
            >
                <FormattedMessage id={'intl-msg:asset.delete'} />
            </Button>
            <Button
                variant={Button.VARIANT_LINK}
                iconName={statusActionButtonIconName}
                disabled={statusActionDisabled}
                autoTrackingKey='detail-view-status-action-button'
                onClick={onStatusActionClick}
                data-cy='detail-view-status-action-button'
            >
                {statusActionButtonLabel}
            </Button>
        </>
    );

    const deletionFooter = (
        <ButtonToolbar>
            <Button
                className='CancelButton'
                autoTrackingKey='intl-msg:asset.delete-confirmation-dialog.button.cancel'
                onClick={closeDeleteDialog}
            >
                <FormattedMessage id={'intl-msg:asset.delete-confirmation-dialog.button.cancel'} />
            </Button>
            <Button
                onClick={deleteAsset}
                className='ConfirmationButton'
                bsStyle={Button.DANGER}
                autoTrackingKey='intl-msg:asset.delete-confirmation-dialog.button.confirm'
                disabled={
                    (hasActiveContracts && !allowAssetDeleteWithActiveContracts && (!isTestTenant || isLatamTenant)) ||
                    modalDeleteButtonDisabled ||
                    deletionErrorMessageCode != null
                }
            >
                <FormattedMessage id={'intl-msg:asset.delete-confirmation-dialog.button.confirm'} />
            </Button>
        </ButtonToolbar>
    );

    const deletionModalContent =
        deletionErrorMessageCode == null ? (
            <div>
                <div className='text-size-16 margin-bottom-15'>
                    <FormattedMessage
                        id={'intl-msg:asset.delete-confirmation-dialog.title'}
                        values={{
                            assetName: updatedAsset.name,
                            b: chunk => <strong>{chunk}</strong>,
                        }}
                    />
                </div>
                <div className='alert alert-danger display-flex flex-column gap-15'>
                    {isLatamTenant ? (
                        ''
                    ) : (
                        <div className='display-flex gap-10'>
                            <span className='text-color-danger text-size-h4 uikit-icon icon-error-sign' />
                            <div>
                                {' '}
                                <strong className='text-size-16'>
                                    <FormattedMessage
                                        id={'intl-msg:asset.delete-confirmation-dialog.content.booked-services.title'}
                                    />
                                </strong>
                                <div>
                                    <FormattedMessage
                                        id={'intl-msg:asset.delete-confirmation-dialog.content.booked-services.hint'}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    <div className='display-flex gap-10'>
                        <span className='text-color-danger text-size-h4 uikit-icon icon-error-sign' />
                        <div>
                            <strong className='text-size-16'>
                                <FormattedMessage
                                    id={'intl-msg:asset.delete-confirmation-dialog.content.deletion-note.title'}
                                />
                            </strong>
                            <div>
                                <FormattedMessage
                                    id={'intl-msg:asset.delete-confirmation-dialog.content.deletion-note.hint'}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <ActiveContractsWarning assetId={updatedAsset.id} deletionMode />
            </div>
        ) : (
            <React.Fragment>
                <FormattedMessage
                    id={'intl-msg:asset.delete-confirmation-dialog.content'}
                    values={{ assetName: updatedAsset.name }}
                />
                <div className='alert alert-danger margin-top-10'>
                    <FormattedMessage
                        id={`intl-msg:asset.delete-confirmation-dialog.content.error.${deletionErrorMessageCode}`}
                    />
                </div>
            </React.Fragment>
        );

    return (
        <>
            <ButtonToolbar className='align-items-center' alignment='space-between'>
                <div className='display-flex gap-5'>{leftActions}</div>
                <Button
                    bsStyle={Button.PRIMARY}
                    className={buttonClassNames}
                    disabled={saveButtonDisabled}
                    autoTrackingKey='intl-msg:button.submit'
                    onClick={submitButtonClickHandler}
                    data-cy='detail-view-confirm-button'
                >
                    <FormattedMessage id={'intl-msg:button.submit'} />
                </Button>
            </ButtonToolbar>
            <Dialog
                showCloseButton
                bodyClassName=''
                bsSize='sm'
                body={deletionModalContent}
                footer={deletionFooter}
                show={displayDeleteDialog}
                useOverflow={true}
            />
        </>
    );
};
